import times from 'lodash/times'
import React, { FC, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import SVGStar from '@Assets/SVG/Star'
import { Abundance } from '@Components/Badges'
import { up } from '@Globals/Style/breakpoints'
import { flexCenterChildren } from '@Globals/Style/mixins'

interface StarPartialProps {
  /** fraction between 0 and 1 */
  value: number
}

const StarBackground = styled.div`
  svg {
    fill: ${({ theme }) => theme.colors.greyLight};
  }
`

const StarForeground = styled.div<StarPartialProps>`
  width: ${({ value }) => `${value * 100}%`} !important;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  overflow: hidden;
  svg {
    fill: ${({ theme }) => theme.colors.primary}
  }
`

const PartialStar = styled.div`
  position: relative;
  margin-right: 2px;
  width: 1em;
  height: 1em;
  line-height: 1;
  vertical-align: top;
`

const IconWrap = styled.div`
  height: 100%;
  width: 1em;
  ${flexCenterChildren}
`

const StarPartial = ({ value }: StarPartialProps) => (
  <PartialStar>
    <StarBackground>
      <IconWrap>
        <SVGStar />
      </IconWrap>
    </StarBackground>
    <StarForeground value={value}>
      <IconWrap>
        <SVGStar />
      </IconWrap>
    </StarForeground>
  </PartialStar>
)

interface StarRatingProps {
  abundance?: ReactNode
  className?: string
  /** Range 1-5, can have decimals */
  rating: number
}

const Star = styled(PartialStar)<{ variant: 'full' | 'empty' }>`
  ${({ theme, variant }) => {
    if (variant === 'full') {
      return css`
        svg {
          fill: ${theme.colors.primary};
        }
      `
    }
    return css`
      svg {
        fill: ${theme.colors.greyLight}
      }
    `
  }}
`

const StarEmpty = () => (
  <Star variant="empty">
    <IconWrap>
      <SVGStar />
    </IconWrap>
  </Star>
)

const StarFull = () => (
  <Star variant="full">
    <IconWrap>
      <SVGStar />
    </IconWrap>
  </Star>
)

export const StyledStarRating = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  height: 1em;

  ${up('tablet')} {
    font-size: 20px;
  }
`

export const StarRating: FC<StarRatingProps> = ({
  abundance,
  className,
  rating,
}: StarRatingProps) => (
  <StyledStarRating className={className}>
    {times(5, (count) => {
      const current = rating - count

      if (current < 0) {
        return <StarEmpty key={count} />
      }

      if (current >= 1) {
        return <StarFull key={count} />
      }

      return <StarPartial key={count} value={current} />
    })}
    {abundance && abundance > 0 && <Abundance>{abundance}</Abundance>}
  </StyledStarRating>
)
